import api from "@/config/api";
import { handleApiError } from "@/utils/ErrorHandler";
import { createPaginationModule, defaultPagination } from "@/store/common/pagination";
import LoggerService from "@/services/LoggerService";
import { getLocationId } from "@/utils/SessionDataHandler";

const orgsPagination = createPaginationModule('ORGANIZATIONS');

const state = () => ({
    organizations: [],
    singleOrganization: null,
    pagination: defaultPagination(),
    adminsByOrg: {},
    organizationSummary: [],
    organizationSummaryMeta: null,
    organizationSummaryLoading: false,
    organizationSummaryError: null,
    loading: false,
    error: null,
    lastSearch: "", // ✅ Keep search term for reuse
});

const mutations = {
    ...orgsPagination.mutations, // ✅ Module-specific pagination mutations

    SET_ORGANIZATIONS(state, payload) {
        state.organizations = payload;
    },

    SET_SINGLE_ORGANIZATION(state, payload) {
        state.singleOrganization = payload;
    },

    // admins mutations
    SET_ADMINS_FOR_ORG(state, { orgId, admins }) {
        state.adminsByOrg = { ...state.adminsByOrg, [orgId]: admins };
    },
    CLEAR_ADMINS_FOR_ORG(state, orgId) {
        const copy = { ...state.adminsByOrg };
        delete copy[orgId];
        state.adminsByOrg = copy;
    },

    SET_LOADING(state, value) {
        state.loading = value;
    },
    SET_ERROR(state, error) {
        state.error = error;
    },
    SET_LAST_SEARCH(state, search) {
        state.lastSearch = search;
    },
    SET_ORGANIZATION_SUMMARY(state, { data, meta }) {
        state.organizationSummary = data || [];
        state.organizationSummaryMeta = meta || null;
    },
    SET_ORGANIZATION_SUMMARY_LOADING(state, value) {
        state.organizationSummaryLoading = value;
    },
    SET_ORGANIZATION_SUMMARY_ERROR(state, error) {
        state.organizationSummaryError = error;
    },
};

const actions = {
    async fetchOrganizationSummary({ commit }) {
        commit("SET_ORGANIZATION_SUMMARY_LOADING", true);
        commit("SET_ORGANIZATION_SUMMARY_ERROR", null);

        try {
            const locationId = getLocationId();
            const response = await api.get("/organizations/summary", {
                params: locationId ? { location_id: locationId } : {},
            });
            const payload = response.data || {};
            const data = Array.isArray(payload.data) ? payload.data : [];
            commit("SET_ORGANIZATION_SUMMARY", {
                data,
                meta: payload.meta || null,
            });
            return data;
        } catch (error) {
            const message = handleApiError
                ? handleApiError(error, "Failed to fetch organization summary")
                : error.message || "Failed to fetch organization summary";
            commit("SET_ORGANIZATION_SUMMARY_ERROR", message);
            throw new Error(message);
        } finally {
            commit("SET_ORGANIZATION_SUMMARY_LOADING", false);
        }
    },
    /**
     * ✅ Fetch organizations with pagination and search support
     * API: /organizations?page_number=1&page_size=10&search=
     */
    async fetchOrganizations({ commit }, params = {}) {
        const {
            search = "",
            pageNumber = 1,
            pageSize = 10,
            status = null,
            signup_date_from = null,
            signup_date_to = null,
            balance_min = null,
            balance_max = null,
            organization_id = null,
            location_id = null,
            location_count = null,
            location_status = null,
            persons_count = null,
        } = params;

        commit("SET_LOADING", true);
        commit("SET_ERROR", null);
        commit("SET_LAST_SEARCH", search);

        try {
            const queryParams = new URLSearchParams();
            queryParams.set('page_number', pageNumber);
            queryParams.set('page_size', pageSize);
            if (search) queryParams.set('search', search);
            if (status) queryParams.set('status', status);
            if (signup_date_from) queryParams.set('signup_date_from', signup_date_from);
            if (signup_date_to) queryParams.set('signup_date_to', signup_date_to);
            if (balance_min !== null && balance_min !== '') queryParams.set('balance_min', balance_min);
            if (balance_max !== null && balance_max !== '') queryParams.set('balance_max', balance_max);
            if (organization_id) queryParams.set('organization_id', organization_id);
            if (location_id) queryParams.set('location_id', location_id);
            if (location_count !== null && location_count !== '') queryParams.set('location_count', location_count);
            if (location_status) queryParams.set('location_status', location_status);
            if (persons_count !== null && persons_count !== '') queryParams.set('persons_count', persons_count);

            const response = await api.get(`/organizations?${queryParams.toString()}`);

            console.log('Full API Response:', response);
            console.log('Response Data:', response.data);

            // ✅ Handle the actual API response structure: { success, data: [...], pagination: {...} }
            const apiData = response.data;
            const organizations = apiData?.data || [];
            const paginationData = apiData?.pagination;

            console.log('Parsed Organizations:', organizations);
            console.log('Pagination Data:', paginationData);

            commit("SET_ORGANIZATIONS", organizations);

            // ✅ Update pagination info
            if (paginationData) {
                commit(orgsPagination.SET_PAGINATION, {
                    currentPage: paginationData.current_page,
                    totalPages: paginationData.total_pages,
                    totalRecords: paginationData.total_records,
                    pageSize: paginationData.page_size,
                    hasPrevious: paginationData.has_previous,
                    hasNext: paginationData.has_next,
                });
            }

            return organizations;
        } catch (error) {
            console.error('Fetch Organizations Error:', error);
            const message = handleApiError
                ? handleApiError(error, "Failed to fetch organizations")
                : error.message || "Failed to fetch organizations";
            commit("SET_ERROR", message);
            throw new Error(message);
        } finally {
            commit("SET_LOADING", false);
        }
    },

    /**
     * ✅ Handle page change without resetting search term
     */
    async changeOrganizationPage({ state, dispatch }, { page, pageSize }) {
        const currentSearch = state.lastSearch || "";
        await dispatch("fetchOrganizations", {
            search: currentSearch,
            pageNumber: page,
            pageSize,
        });
    },

    /**
     * ✅ Update search term and reset to page 1
     */
    async updateOrganizationSearch({ dispatch, state }, search) {
        await dispatch("fetchOrganizations", {
            search,
            pageNumber: 1,
            pageSize: state.pagination.pageSize || 10,
        });
    },

    async fetchOrganizationById({ commit }, id) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);

        try {
            const { data } = await api.get(`/organizations/${id}`);

            if (data?.output) {
                commit("SET_SINGLE_ORGANIZATION", data.output);
                return data.output;
            }
            return null;
        } catch (error) {
            const message = handleApiError
                ? handleApiError(error, "Failed to fetch organization details")
                : error.message || "Failed to fetch organization details";
            commit("SET_ERROR", message);
            throw new Error(message);
        } finally {
            commit("SET_LOADING", false);
        }
    },

    async updateOrganization({ commit, state, rootGetters }, { id, payload }) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);

        try {
            const { data } = await api.put(`/organizations/${id}`, payload, {
                headers: { "Content-Type": "application/json" },
            });

            if (data?.output) {
                commit("SET_SINGLE_ORGANIZATION", data.output);

                // 🔄 Update it inside organizations list if present
                const orgIndex = state.organizations.findIndex(
                    (org) => org.organizationid === id
                );
                if (orgIndex !== -1) {
                    state.organizations.splice(orgIndex, 1, data.output);
                }

                // Log organization update
                const currentUser = rootGetters.stSessionData;
                await LoggerService.logDataOperation(
                    'UPDATE',
                    'organization',
                    id,
                    {
                        updatedFields: Object.keys(payload),
                        organizationName: data.output?.organizationname
                    },
                    currentUser?.personid
                );

                return data.output;
            }
            return null;
        } catch (error) {
            const message = handleApiError
                ? handleApiError(error, "Failed to update organization")
                : error.message || "Failed to update organization";
            commit("SET_ERROR", message);
            
            // Log failed update
            const currentUser = rootGetters.stSessionData;
            await LoggerService.logDataOperation(
                'UPDATE_FAILED',
                'organization',
                id,
                { error: message, payload },
                currentUser?.personid
            );
            
            throw new Error(message);
        } finally {
            commit("SET_LOADING", false);
        }
    },

    async impersonateLocation({ commit }, locationId) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);
        try {
            const response = await api.post('/managelocationajax', {
                cmd: 'impersonatelocation',
                locationid: locationId,
            });
            return response.data?.output || response.data;
        } catch (error) {
            const message = handleApiError
                ? handleApiError(error, "Failed to impersonate location")
                : error.message || "Failed to impersonate location";
            commit("SET_ERROR", message);
            throw new Error(message);
        } finally {
            commit("SET_LOADING", false);
        }
    },

    async fetchOrgQuickInfo({ commit }, organizationId) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);
        try {
            const response = await api.post('/managelocationajax', {
                cmd: 'superadminorginfo',
                organizationid: organizationId,
            });
            return response.data?.output || response.data;
        } catch (error) {
            const message = handleApiError
                ? handleApiError(error, "Failed to fetch organization info")
                : error.message || "Failed to fetch organization info";
            commit("SET_ERROR", message);
            throw new Error(message);
        } finally {
            commit("SET_LOADING", false);
        }
    },

    async fetchAdminsForOrganization({ commit }, orgId) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);

        try {
            const resp = await api.get(`/organizations/${orgId}/admins`);
            let rawList = resp.data?.output ?? resp.data?.data ?? resp.data ?? [];

            if (!Array.isArray(rawList) && rawList && Array.isArray(rawList.data)) {
                rawList = rawList.data;
            }

            return rawList;
        } catch (error) {
            const message = handleApiError
                ? handleApiError(error, "Failed to fetch admins")
                : error.message || "Failed to fetch admins";
            commit("SET_ERROR", message);
            throw new Error(message);
        } finally {
            commit("SET_LOADING", false);
        }
    },

    async assignAdminsToOrganization({ commit, rootGetters }, { orgId, userIds }) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);

        try {
            const { data } = await api.post(
                `/organizations/${orgId}/admins`,
                { user_ids: userIds },
                {
                    headers: { "Content-Type": "application/json" },
                }
            );

            const raw = data?.data ?? data?.output ?? data ?? [];
            const arr = Array.isArray(raw) ? raw : raw.data ?? [];

            const admins = (arr || []).map((a) => ({
                id: a.userid ?? a.id ?? a.user_id ?? a.userId,
                name: a.name || a.username || a.displayname || `${a.first_name ?? ""} ${a.last_name ?? ""}`.trim(),
                description: a.email || "",
                raw: a,
            }));

            if (admins.length) {
                commit("SET_ADMINS_FOR_ORG", { orgId, admins });
            }

            if (data?.output) {
                commit("SET_SINGLE_ORGANIZATION", data.output);
            }

            // Log admin assignment
            const currentUser = rootGetters.stSessionData;
            await LoggerService.logDataOperation(
                'UPDATE',
                'organization_admins',
                orgId,
                {
                    assignedUserIds: userIds,
                    adminCount: admins.length
                },
                currentUser?.personid
            );

            return data;
        } catch (error) {
            const message = handleApiError
                ? handleApiError(error, "Failed to assign admins")
                : error.message || "Failed to assign admins";
            commit("SET_ERROR", message);
            
            // Log failed assignment
            const currentUser = rootGetters.stSessionData;
            await LoggerService.logDataOperation(
                'UPDATE_FAILED',
                'organization_admins',
                orgId,
                { error: message, userIds },
                currentUser?.personid
            );
            
            throw new Error(message);
        } finally {
            commit("SET_LOADING", false);
        }
    },
};

const getters = {
    allOrganizations: (state) => state.organizations,
    allLocations: (state) =>
        state.organizations.flatMap((org) =>
            org.locations ? org.locations.map((loc) => ({ ...loc, orgId: org.organizationid })) : []
        ),
    getOrganizationById: (state) => (id) =>
        state.organizations?.find((org) => org.organizationid === id),
    singleOrganization: (state) => state.singleOrganization,
    isOrganizationsLoading: (state) => state.loading,
    getOrganizationsError: (state) => state.error,
    organizationsPagination: (state) => state.pagination, // ✅ Renamed for clarity
    getAdminsByOrg: (state) => (orgId) => state.adminsByOrg[orgId] ?? [],
    organizationSummary: (state) => state.organizationSummary,
    organizationSummaryMeta: (state) => state.organizationSummaryMeta,
    organizationSummaryLoading: (state) => state.organizationSummaryLoading,
    organizationSummaryError: (state) => state.organizationSummaryError,
};

export default {
    state,
    mutations,
    actions,
    getters,
}; 